/**
 * Cycle Template store
 *
 * CycleTemplates are initialization blueprints applied once when a cycle is
 * created. After that the cycle owns the seeded values; the template has no
 * further authority over it.
 *
 * Applying one is the server's job (migration 1040): create_cycle and the
 * triggers on degap_cycles / tracks take the seed once and record it on the
 * cycle (applied_template_id, template_applied_at, template_seed). This store
 * only reads and edits templates; editing one never reaches a cycle already
 * made from it.
 *
 * Contrast with Workflows (runtime orchestration policy, always live).
 *
 * Canon: "Workflow is always live. Templates are applied once."
 */

import { create } from 'zustand';
import type {
  CycleTemplate,
  CycleTemplateStatus,
  TemplateAttributePreset,
  TemplateParticipantPreset,
  TemplateGroupPreset,
} from '../../types/prioritization/cycleTemplate';
import {
  getCurrentWorkspaceId,
  getSafeSupabaseClient,
  isSupabaseAvailable,
  generateUUID,
  isInDemoMode,
} from '../../lib/supabaseSync';
import {
  retryWhenWorkspaceContextChanges,
  storeWorkspaceId,
  workspaceSeedBlocker,
} from '../../lib/workspaceSeedGuard';

const TABLE_NAME = 'cycle_templates';

/** Built-in template seeds — applied when no templates exist in the workspace. */
export const CYCLE_TEMPLATE_SEEDS: Array<{
  name: string;
  description: string;
  defaultAttributes: TemplateAttributePreset[];
  defaultParticipants: TemplateParticipantPreset[];
  defaultGroups: TemplateGroupPreset[];
}> = [
  {
    name: 'Impact / Effort',
    description: 'Seeds cycles with Impact (fibonacci) and Effort (t-shirt sizes) criteria. Good baseline for most prioritization workflows.',
    defaultAttributes: [
      { attributeKey: 'impact', required: true, defaultRange: { type: 'fibonacci', options: [1, 2, 3, 5, 8, 13, 21] } },
      { attributeKey: 'effort', required: true, defaultRange: { type: 'tshirt', options: ['XS', 'S', 'M', 'L', 'XL', 'XXL'] } },
    ],
    defaultParticipants: [],
    defaultGroups: [],
  },
  {
    name: 'Lightweight Scoring',
    description: 'Seeds cycles with Impact and Effort using simple Low/Medium/High scales. Best for ad-hoc or exploratory prioritization.',
    defaultAttributes: [
      { attributeKey: 'impact', required: true, defaultRange: { type: 'select', options: ['Low', 'Medium', 'High'] } },
      { attributeKey: 'effort', required: false, defaultRange: { type: 'select', options: ['Low', 'Medium', 'High'] } },
    ],
    defaultParticipants: [],
    defaultGroups: [],
  },
  {
    name: 'Blank',
    description: 'No pre-seeded attributes, participants, or groups. Configure everything manually on each cycle.',
    defaultAttributes: [],
    defaultParticipants: [],
    defaultGroups: [],
  },
];

/** DB row shape (snake_case) before camelCase conversion. */
interface CycleTemplateRow {
  id: string;
  workspace_id: string | null;
  name: string;
  description: string | null;
  status: CycleTemplateStatus;
  default_attributes: TemplateAttributePreset[];
  default_participants: TemplateParticipantPreset[];
  default_groups: TemplateGroupPreset[];
  /** 769 — optional so rows read before the column exists still map. */
  displacement_discipline?: boolean | null;
  created_at: string;
  created_by: string | null;
  updated_at: string;
}

function rowToTemplate(row: CycleTemplateRow): CycleTemplate {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    name: row.name,
    description: row.description,
    status: row.status,
    defaultAttributes: Array.isArray(row.default_attributes) ? row.default_attributes : [],
    defaultParticipants: Array.isArray(row.default_participants) ? row.default_participants : [],
    defaultGroups: Array.isArray(row.default_groups) ? row.default_groups : [],
    displacementDiscipline: row.displacement_discipline === true,
    createdAt: new Date(row.created_at),
    createdBy: row.created_by ?? 'system',
    updatedAt: new Date(row.updated_at),
  };
}

function templateToRow(t: CycleTemplate): CycleTemplateRow {
  return {
    id: t.id,
    workspace_id: t.workspaceId,
    name: t.name,
    description: t.description ?? null,
    status: t.status,
    default_attributes: t.defaultAttributes,
    default_participants: t.defaultParticipants,
    default_groups: t.defaultGroups,
    // Sent only when on (the column defaults false), so saving a template that
    // never uses the rule does not depend on migration 769 having landed.
    ...(t.displacementDiscipline ? { displacement_discipline: true } : {}),
    created_at: t.createdAt.toISOString(),
    created_by: t.createdBy,
    updated_at: t.updatedAt.toISOString(),
  };
}

export interface CycleTemplateStoreState {
  templates: CycleTemplate[];
  /** The workspace `templates` were read for; null before a read lands (and in demo mode). */
  loadedWorkspaceId: string | null;
  isLoading: boolean;
  loadError: string | null;

  fetchTemplates: () => Promise<void>;

  createTemplate: (data: {
    name: string;
    description?: string;
    createdBy: string;
    defaultAttributes?: TemplateAttributePreset[];
    defaultParticipants?: TemplateParticipantPreset[];
    defaultGroups?: TemplateGroupPreset[];
    displacementDiscipline?: boolean;
  }) => Promise<CycleTemplate | null>;

  updateTemplate: (
    id: string,
    updates: Partial<Pick<CycleTemplate, 'name' | 'description' | 'status' | 'defaultAttributes' | 'defaultParticipants' | 'defaultGroups' | 'displacementDiscipline'>>
  ) => Promise<{ success: boolean; error?: string }>;

  archiveTemplate: (id: string) => Promise<{ success: boolean; error?: string }>;
}

// One seeding run per workspace at a time: concurrent reads of an empty
// workspace wait for its rows instead of each seeding the built-in set.
let _seeding: { workspaceId: string | null; rows: Promise<CycleTemplate[]> } | null = null;
// Each fetch takes a number; only the latest may write the store, so a read
// that started in one workspace cannot land after a read for the next.
let _fetchSeq = 0;

/** Insert the built-in templates into `workspaceId`. */
async function seedBuiltInTemplates(workspaceId: string | null): Promise<CycleTemplate[]> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return [];
  const now = new Date();
  const seeded: CycleTemplate[] = [];
  for (const seed of CYCLE_TEMPLATE_SEEDS) {
    const t: CycleTemplate = {
      id: generateUUID(),
      workspaceId,
      name: seed.name,
      description: seed.description,
      status: 'active',
      defaultAttributes: seed.defaultAttributes,
      defaultParticipants: seed.defaultParticipants,
      defaultGroups: seed.defaultGroups,
      displacementDiscipline: false,
      createdAt: now,
      createdBy: 'system',
      updatedAt: now,
    };
    const { data: inserted, error: insertError } = await supabase
      .from(TABLE_NAME)
      .insert(templateToRow(t))
      .select()
      .single();
    seeded.push(inserted ? rowToTemplate(inserted as unknown as CycleTemplateRow) : t);
    if (insertError) {
      console.warn('[cycleTemplateStore] Failed to seed template:', seed.name, insertError.message);
    }
  }
  return seeded;
}

/** A read that found the workspace in flux reads again once it settles. */
function readTemplatesAgainWhenContextChanges(): void {
  retryWhenWorkspaceContextChanges('cycle-templates', () => {
    void useCycleTemplateStore.getState().fetchTemplates();
  });
}

export const useCycleTemplateStore = create<CycleTemplateStoreState>()((set, get) => ({
  templates: [],
  loadedWorkspaceId: null,
  isLoading: false,
  loadError: null,

  fetchTemplates: async () => {
    if (!isSupabaseAvailable()) {
      set({ loadError: 'Supabase not configured' });
      return;
    }
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ isLoading: false, loadError: 'Supabase not configured' });
      return;
    }

    const seq = ++_fetchSeq;
    // The workspace this read runs against (null in demo mode, as the rows are written).
    const workspaceId = storeWorkspaceId();
    // Rows held for another workspace are never shown while this one loads.
    set({
      isLoading: true,
      loadError: null,
      ...(get().loadedWorkspaceId !== workspaceId ? { templates: [], loadedWorkspaceId: null } : {}),
    });

    if (!workspaceId && !isInDemoMode()) {
      // No workspace yet: an unfiltered read would show every template this person
      // can see, and an empty one would seed rows with no workspace. See nothing.
      set({ templates: [], loadedWorkspaceId: null, isLoading: false, loadError: null });
      readTemplatesAgainWhenContextChanges();
      return;
    }

    let query = supabase.from(TABLE_NAME).select('*').order('name', { ascending: true });
    if (workspaceId) {
      query = query.or(`workspace_id.eq.${workspaceId},workspace_id.is.null`);
    }

    const { data, error } = await query;
    if (seq !== _fetchSeq) return; // a newer read owns the store
    if (storeWorkspaceId() !== workspaceId) {
      // The workspace changed during the read: these rows answer for the old one.
      set({ templates: [], loadedWorkspaceId: null, isLoading: false });
      readTemplatesAgainWhenContextChanges();
      return;
    }
    if (error) {
      set({ isLoading: false, loadError: error.message });
      return;
    }

    let templates: CycleTemplate[] = (data ?? []).map((row) => rowToTemplate(row as unknown as CycleTemplateRow));

    // Seed built-in templates if the workspace has none yet — only into the workspace
    // the empty read ran against, while it is still the one in force for this person.
    if (templates.length === 0) {
      if (_seeding && _seeding.workspaceId === workspaceId) {
        templates = await _seeding.rows;
      } else {
        const blocker = workspaceSeedBlocker(workspaceId);
        if (blocker) {
          console.log('[cycleTemplateStore] Not seeding built-in templates:', blocker);
          set({ templates: [], loadedWorkspaceId: workspaceId, isLoading: false, loadError: null });
          readTemplatesAgainWhenContextChanges();
          return;
        }
        const run = { workspaceId, rows: seedBuiltInTemplates(workspaceId) };
        _seeding = run;
        try {
          templates = await run.rows;
        } finally {
          if (_seeding === run) _seeding = null;
        }
      }
      if (seq !== _fetchSeq) return;
      if (storeWorkspaceId() !== workspaceId) {
        set({ templates: [], loadedWorkspaceId: null, isLoading: false });
        readTemplatesAgainWhenContextChanges();
        return;
      }
    }

    set({ templates, loadedWorkspaceId: workspaceId, isLoading: false, loadError: null });
  },

  createTemplate: async (data) => {
    if (!isSupabaseAvailable()) return null;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    const workspaceId = isInDemoMode() ? null : getCurrentWorkspaceId();
    const now = new Date();
    const t: CycleTemplate = {
      id: generateUUID(),
      workspaceId,
      name: data.name,
      description: data.description ?? null,
      status: 'active',
      defaultAttributes: data.defaultAttributes ?? [],
      defaultParticipants: data.defaultParticipants ?? [],
      defaultGroups: data.defaultGroups ?? [],
      displacementDiscipline: data.displacementDiscipline ?? false,
      createdAt: now,
      createdBy: data.createdBy,
      updatedAt: now,
    };

    const { data: inserted, error } = await supabase
      .from(TABLE_NAME)
      .insert(templateToRow(t))
      .select()
      .single();

    if (error) {
      console.warn('[cycleTemplateStore] Failed to create template:', error.message);
      return null;
    }

    const created = inserted ? rowToTemplate(inserted as unknown as CycleTemplateRow) : t;
    set((s) => ({ templates: [...s.templates, created] }));
    return created;
  },

  updateTemplate: async (id, updates) => {
    if (!isSupabaseAvailable()) return { success: false, error: 'Supabase not configured' };
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Supabase not configured' };

    const current = get().templates.find((t) => t.id === id);
    if (!current) return { success: false, error: 'Template not found' };

    const updated: CycleTemplate = { ...current, ...updates, updatedAt: new Date() };
    const row = templateToRow(updated);
    // Turning the rule off has to be written explicitly (templateToRow omits false).
    if (current.displacementDiscipline && !updated.displacementDiscipline) {
      row.displacement_discipline = false;
    }
    const { error } = await supabase
      .from(TABLE_NAME)
      .update(row)
      .eq('id', id);

    if (error) return { success: false, error: error.message };

    set((s) => ({ templates: s.templates.map((t) => (t.id === id ? updated : t)) }));
    return { success: true };
  },

  archiveTemplate: async (id) => {
    return get().updateTemplate(id, { status: 'archived' });
  },
}));

// Convenience selector: only active templates
export function selectActiveTemplates(templates: CycleTemplate[]): CycleTemplate[] {
  return templates.filter((t) => t.status !== 'archived');
}
